"use client";

import { useState } from "react";
import type { ColorSwatchGroupWithImagesProps } from "../../elements/color-swatch/color-swatch.utils";
import { ProductCardB2B, type ProductCardB2BProps } from "./product-card-b2b";

export interface ProductCardB2BClientProps
  extends Omit<ProductCardB2BProps, "colorSwatches"> {
  colorSwatches?: ColorSwatchGroupWithImagesProps;
  onSelectedSwatchChange?: (swatchId: string) => void;
  /** Display data for each color; an absent badge clears the previous color's badge. */
  variantData?: Record<string, Pick<ProductCardB2BProps, "price" | "badges">>;
}

export function ProductCardB2BClient(
  props: Readonly<ProductCardB2BClientProps>
) {
  const {
    colorSwatches,
    image,
    microcopies,
    onSelectedSwatchChange,
    variantData,
    ...rest
  } = props;

  const [selectedSwatchId, setSelectedSwatchId] = useState(
    colorSwatches?.selectedId ?? colorSwatches?.swatches[0]?.id
  );

  const selectedSwatch =
    colorSwatches?.swatches.find((s) => s.id === selectedSwatchId) ??
    colorSwatches?.swatches[0];
  const selectedVariant = selectedSwatch
    ? variantData?.[selectedSwatch.id]
    : undefined;
  const displayedImageSrc = selectedSwatch?.imageUrl ?? image.src;
  const displayedName = selectedSwatch?.name ?? microcopies.name;
  const displayedHref = selectedSwatch?.href ?? rest.href;

  return (
    <ProductCardB2B
      {...rest}
      badges={selectedVariant ? selectedVariant.badges : rest.badges}
      colorSwatches={
        colorSwatches
          ? {
              ...colorSwatches,
              swatches: colorSwatches.swatches.map(
                ({ imageUrl: _, name: __, href: ___, ...s }) => s
              ),
              selectedId: selectedSwatch?.id,
              onSelect: (swatchId) => {
                setSelectedSwatchId(swatchId);
                onSelectedSwatchChange?.(swatchId);
              },
            }
          : undefined
      }
      href={displayedHref}
      image={{
        ...image,
        src: displayedImageSrc,
        alt: selectedSwatch?.name ?? image.alt,
      }}
      microcopies={{ ...microcopies, name: displayedName }}
      price={selectedVariant?.price ?? rest.price}
    />
  );
}
